import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { useTitle } from "../components/Layout.js";
import { api, ApiError } from "../data/api.js";
import { attachDeviceSessions, refreshIdentity } from "../data/identity.js";

// Přihlašovací odkaz `/{jazyk}/l/{token}` (REQ-032). Token z adresy hned zmizí (zůstane jen
// v paměti stránky) a přihlásí až tlačítko: poštovní klient, který odkazy otevírá předem, nikoho
// nepřihlásí. Po přihlášení se k identitě připojí sezení z tohoto zařízení.

type State = "ready" | "working" | "gone" | "error";

export function LoginPage() {
  const { lang = "cs", token: param } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const intl = useIntl();
  const [token] = useState<string | null>(() => param ?? (location.state as { token?: string } | null)?.token ?? null);
  const [state, setState] = useState<State>(token === null ? "gone" : "ready");
  useTitle(intl.formatMessage({ id: "login.title" }));

  useEffect(() => {
    if (param !== undefined) void navigate(`/${lang}/l`, { replace: true, state: { token: param } });
    const metas = [
      Object.assign(document.createElement("meta"), { name: "robots", content: "noindex, nofollow" }),
      Object.assign(document.createElement("meta"), { name: "referrer", content: "no-referrer" }),
    ];
    for (const m of metas) document.head.append(m);
    return () => {
      for (const m of metas) m.remove();
    };
  }, [param, lang, navigate]);

  async function signIn() {
    if (token === null) return;
    setState("working");
    try {
      const r = await api.identityClaim(token);
      await attachDeviceSessions();
      await refreshIdentity();
      void navigate(r.return_path, { replace: true });
    } catch (e) {
      setState(e instanceof ApiError && (e.status === 410 || e.status === 400) ? "gone" : "error");
    }
  }

  if (state === "gone") {
    return (
      <div className="stack" data-testid="login-gone">
        <h1>
          <FormattedMessage id="login.title" />
        </h1>
        <p className="muted">
          <FormattedMessage id="login.gone" />
        </p>
        <Link className="button" to={`/${lang}/me`}>
          <FormattedMessage id="login.goMe" />
        </Link>
      </div>
    );
  }
  return (
    <div className="stack" data-testid="login">
      <h1>
        <FormattedMessage id="login.title" />
      </h1>
      <p className="muted">
        <FormattedMessage id="login.lead" />
      </p>
      {state === "error" ? (
        <p className="small" role="alert">
          <FormattedMessage id="error.generic" />
        </p>
      ) : null}
      <button type="button" className="button" disabled={state === "working"} onClick={() => void signIn()} data-testid="login-continue">
        <FormattedMessage id="login.button" />
      </button>
    </div>
  );
}
